import React from 'react';
import {
  ApolloClient,
  ApolloProvider,
  defaultDataIdFromObject,
  InMemoryCache,
} from '@apollo/client';
import { RestLink } from 'apollo-link-rest';
import styled from 'styled-components';

import { colors } from 'bench-style-constants';
import env from 'bench-environment';

import { ReviewHistoryMonthlyQuery, ReviewHistoryPeriodQuery } from './queries';

import HistoryContainer from './HistoryContainer';
import ReviewFormContainer from './ReviewFormContainer';

const getJournalEntryAmount = dataEntries =>
  dataEntries
    .filter(entry => entry.entryType === 'CREDIT')
    .map(entry => Math.abs(entry.amount))
    .reduce((a, b) => a + b, 0);

const link = new RestLink({
  uri: `${env.location.apiRoot}/api/v1/`,
  credentials: 'include',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  typePatcher: {
    ReviewHistoryMonth: (sourceData: any): any => {
      const data = { ...sourceData };

      if (data.reviews) {
        data.reviews = data.reviews.map(review => ({
          __typename: 'ReviewHistoryPeriod',
          ...review,
        }));
      }

      return data;
    },
    ReviewChecks: (sourceData: any): any => {
      const data = { ...sourceData };

      if (data.journalEntries.length > 0) {
        data.journalEntries = data.journalEntries.map(journalEntry => ({
          __typename: 'JournalEntries',
          ...journalEntry,
          // A total amount isn't calculated for us on the server so lets inject it!
          amount: getJournalEntryAmount(journalEntry.dataEntries),
        }));
      }

      return data;
    },
    JournalEntry: (sourceData: any): any => ({
      ...sourceData,
      amount: getJournalEntryAmount(sourceData.dataEntries),
    }),
  },
});

const client = new ApolloClient({
  link,
  cache: new InMemoryCache({
    dataIdFromObject: object => {
      switch (
        object.__typename // eslint-disable-line no-underscore-dangle
      ) {
        case 'ReviewChecks':
          return `${object.id}-${object.ledgerId}`;
        default:
          return defaultDataIdFromObject(object);
      }
    },
  }),
});

const Wrapper = styled.div`
  min-height: 100vh;
  overflow-y: auto;
  background: ${colors.white};
`;

const FullScreen = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

type ReviewChanges = {
  reviewing: boolean;
  restoreLastChecks: boolean;
  startDate?: string;
  endDate?: string;
};

type AppProps = {
  clientId: string;
  startDate: string;
  endDate: string;
  reviewing: boolean;
  restoreLastChecks: boolean;
  onReviewingChange: (changes: ReviewChanges) => void;
};

const App = ({
  clientId,
  startDate,
  endDate,
  reviewing,
  restoreLastChecks,
  onReviewingChange,
}: AppProps) => (
  <ApolloProvider client={client}>
    <Wrapper>
      {!reviewing ? (
        <HistoryContainer
          clientId={clientId}
          onStartReview={(nextRestoreLastChecks, nextStartDate, nextEndDate) => {
            onReviewingChange({
              reviewing: true,
              restoreLastChecks: nextRestoreLastChecks,
              startDate: nextStartDate,
              endDate: nextEndDate,
            });
          }}
        />
      ) : (
        <FullScreen>
          <ReviewFormContainer
            clientId={clientId}
            startDate={startDate}
            endDate={endDate}
            restoreLastChecks={restoreLastChecks}
            refetchQueries={[
              { query: ReviewHistoryPeriodQuery, variables: { clientId } },
              { query: ReviewHistoryMonthlyQuery, variables: { clientId } },
            ]}
            onCloseReview={() => onReviewingChange({ reviewing: false, restoreLastChecks: false })}
          />
        </FullScreen>
      )}
    </Wrapper>
  </ApolloProvider>
);

export default App;
